
import React, { createContext, useContext, useState, useEffect } from "react";

// Define the context type
type NetworkStatusContextType = {
  isOnline: boolean;
};

// Create the context with a default value
const NetworkStatusContext = createContext<NetworkStatusContextType>({
  isOnline: true 
});

// Hook for consuming the context
export const useNetworkStatus = () => useContext(NetworkStatusContext);

// Provider component that wraps app and provides network status
export const NetworkStatusProvider = ({ children }: { children: React.ReactNode }) => {
  // Initialize with a default value first
  const [isOnline, setIsOnline] = useState<boolean>(true);
  
  // Move all browser API calls to useEffect to avoid issues during SSR or testing
  useEffect(() => {
    // Safe check for browser environment
    const isBrowser = typeof window !== 'undefined' && window.navigator;
    
    if (isBrowser) {
      // Update initial online status
      setIsOnline(navigator.onLine);
      
      // Define event handlers
      const handleOnline = () => setIsOnline(true);
      const handleOffline = () => setIsOnline(false);
      
      // Add event listeners
      window.addEventListener("online", handleOnline);
      window.addEventListener("offline", handleOffline);
      
      // Log initial status
      console.log("NetworkStatus initialized:", navigator.onLine ? "online" : "offline");
      
      // Cleanup function
      return () => {
        window.removeEventListener("online", handleOnline);
        window.removeEventListener("offline", handleOffline);
      };
    }
  }, []);

  return (
    <NetworkStatusContext.Provider value={{ isOnline }}>
      {children}
    </NetworkStatusContext.Provider>
  );
};
